import { motion } from 'framer-motion'
import { FC, ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { REUSE_COLORS, withAlpha } from '@/utils/reuseBrand'
import { useHasRedesign } from '@/utils/redesignSellers'

interface StepCardProps {
  icon: ReactNode
  title: string
  subtitle?: string
  right?: ReactNode
  hidden?: boolean
  className?: string
  children: ReactNode
}

/* Encabezado consistente para cada paso del flujo del tradein: ícono en badge + título
   + subtítulo opcional, con una pequeña animación de entrada. Reemplaza el patrón repetido
   `<h6 className='font-bold text-lg ...'>` que tenía cada card.

   Para los sellers que todavía no tienen el rediseño (ver REDESIGN_SELLERS) se renderiza la
   card anterior — mismo contenido, sin ícono ni animación — así el detalle del trade-in les
   sigue viendo igual que antes. */
const StepCard: FC<StepCardProps> = ({ icon, title, subtitle, right, hidden, className, children }) => {
  const hasRedesign = useHasRedesign()

  if (!hasRedesign) {
    return (
      <div hidden={hidden} className={cn('bg-white rounded-lg shadow-lg p-4', className)}>
        <div className='grid grid-cols-2 border-b-1 mb-2'>
          <div><h6 className='font-bold text-xl pb-2'>{title}</h6></div>
          {right}
        </div>
        {children}
      </div>
    )
  }

  return (
    <motion.div
      hidden={hidden}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
      className={cn('bg-white rounded-xl border border-slate-100 shadow-sm p-4', className)}
    >
      <div className='flex items-center gap-3 pb-3 mb-3 border-b border-slate-100'>
        <span
          className='flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-lg'
          style={{ backgroundColor: withAlpha(REUSE_COLORS.reBlue, 0.12), color: REUSE_COLORS.reBlue }}
        >
          {icon}
        </span>
        <div className='min-w-0 flex-1'>
          <h6 className='font-bold text-base leading-tight text-slate-800 truncate'>{title}</h6>
          {subtitle && <p className='text-tiny text-slate-400 truncate'>{subtitle}</p>}
        </div>
        {right}
      </div>
      {children}
    </motion.div>
  )
}

export default StepCard
